/*
  OA — Image placeholder + swipe helper.

  Product photos are lazy-loaded, so before an image loads its slot used to
  show a blank white frame. The CSS (component-card.css for the mobile card
  swipe, section-main-product.css for the product-page gallery) now paints a
  #f4f4f4 tile with a soft "opening act." watermark behind every image, so the
  gap is branded instead of white.

  This script adds progressive enhancements on top of that CSS. If it does not
  run, images simply show normally and the swipe still scrolls — nothing breaks.

    1. Fade-in: each image fades in once it finishes loading.
    2. Preload: neighbouring images are warmed ahead of time.
    3. Endless loop (card swipe only): the in-card image swipe wraps around —
       swiping past the last image returns to the first, and swiping back from
       the first shows the last, like a continuous carousel. Done by cloning
       the first/last slides at each end and jumping invisibly to the real
       slide once a clone is reached.

  Two surfaces:
    - .oa-swipe            in-card swipe track (collection grids), <990px (mobile + tablet).
    - media-gallery        the product-page gallery, all widths.
*/
(function () {
  'use strict';

  // Active on mobile AND the 3-col tablet range (< 990px) — everywhere the
  // desktop hover secondary-image (min-width: 990px) does NOT apply. Kept in
  // sync with the .oa-swipe media query in component-card.css.
  var MOBILE = window.matchMedia('(max-width: 989px)');

  function warm(img) {
    if (!img || img.dataset.oaWarm) return;
    img.dataset.oaWarm = '1';
    var pre = new Image();
    if (img.sizes) pre.sizes = img.sizes;
    if (img.srcset) pre.srcset = img.srcset;
    var src = img.currentSrc || img.src;
    if (src) pre.src = src;
  }

  function markLoaded(img) {
    if (!img) return;
    var done = function () {
      img.classList.add('oa-loaded');
      // Fade the placeholder watermark out once the real image is painted, so
      // it can't linger in the letterbox margins under an object-fit:contain
      // image. The image's own parent carries the ::before watermark.
      if (img.parentElement) img.parentElement.classList.add('oa-shown');
    };
    if (img.complete && img.naturalWidth > 0) {
      done();
    } else {
      img.addEventListener('load', done, { once: true });
      img.addEventListener('error', done, { once: true });
    }
  }

  function list(root, selector) {
    return Array.prototype.slice.call(root.querySelectorAll(selector));
  }

  /* ---- Load-on-swipe: defer non-first slide images until the card is used ----

     Each collection card renders one <img> per product photo for the in-card
     swipe. On image-heavy catalogues that is ~8 images per card; scrolling +
     "Show More" stacked enough decoded bitmaps to crash mobile Safari (out of
     memory). We park every slide except the first (the one visible at rest) in
     data-* attributes so it does NOT load while the shopper merely scrolls, and
     hydrate a card's images the moment it is actually touched/swiped. Visually
     nothing changes; if this script never runs, images keep their real src and
     load normally (graceful degradation). */

  function defer(img) {
    if (!img || img.dataset.oaDefer) return;
    if (img.complete && img.naturalWidth > 0) return; // already loaded — keep it
    var ss = img.getAttribute('srcset');
    var sc = img.getAttribute('src');
    if (!ss && !sc) return;
    if (ss) { img.setAttribute('data-oa-srcset', ss); img.removeAttribute('srcset'); }
    if (sc) { img.setAttribute('data-oa-src', sc); img.removeAttribute('src'); }
    img.dataset.oaDefer = '1';
  }

  function hydrate(img) {
    if (!img || !img.dataset.oaDefer) return;
    var ss = img.getAttribute('data-oa-srcset');
    var sc = img.getAttribute('data-oa-src');
    if (ss) { img.setAttribute('srcset', ss); img.removeAttribute('data-oa-srcset'); }
    if (sc) { img.setAttribute('src', sc); img.removeAttribute('data-oa-src'); }
    delete img.dataset.oaDefer;
  }

  function hydrateTrack(track) {
    if (!track || track.dataset.oaHydrated) return;
    track.dataset.oaHydrated = '1';
    list(track, 'img').forEach(hydrate);
  }

  // Keep the first TWO slides loadable and defer the rest until the card is
  // swiped. Slide 0 is visible at rest; slide 1 is the "next" photo, which
  // setupTrack warms so the first swipe feels instant (light preload). The
  // remaining photos still load only on touch. Guarded per-track so it always
  // runs before the endless-loop clones are added (idempotent across re-scans).
  function deferSwipeImages(root) {
    list(root || document, '.oa-swipe').forEach(function (track) {
      if (track.dataset.oaDeferred) return;
      track.dataset.oaDeferred = '1';
      var slides = list(track, '.oa-swipe__slide');
      for (var i = 2; i < slides.length; i++) {
        defer(slides[i].querySelector('img'));
      }
    });
  }

  // Hydrate a card's deferred images the first time it is touched (= swipe intent).
  function onSwipeIntent(e) {
    var t = e.target;
    var track = t && t.closest ? t.closest('.oa-swipe') : null;
    if (track) hydrateTrack(track);
  }
  document.addEventListener('touchstart', onSwipeIntent, { passive: true });
  document.addEventListener('pointerdown', onSwipeIntent, { passive: true });
  // Laptop in a narrow window: a trackpad swipe is a wheel/scroll, never a
  // touch or pointerdown - photos 3+ stayed unloaded there. scroll does not
  // bubble, so listen in the capture phase (e.target is the track itself).
  document.addEventListener('wheel', onSwipeIntent, { passive: true });
  document.addEventListener('scroll', onSwipeIntent, { passive: true, capture: true });


  /* One slide per gesture.
     `scroll-snap-stop: always` is set on every slide and is supported, but
     WebKit does not honour it during MOMENTUM scrolling — a firm flick on iOS
     therefore flies past several photos. This takes over at the end of the
     gesture: remember the slide the finger started on, and on touchend go to
     exactly the neighbouring one, never further.
     Snapping is switched off for the duration of the correction, otherwise the
     browser's own snap animation fights the programmatic scroll; it is restored
     afterwards so a slow drag still snaps natively. Anything below the distance
     threshold counts as a tap/wobble and is left alone (the slides are links —
     a tap must still open the product). */
  // Max dashes visible in a card's swipe indicator (keep in sync with the
  // nth-child(6) selector + the '5' in component-card.css).
  var DOT_WINDOW = 5;

  function oneSlidePerSwipe(track, widthFn, bySlide) {
    var startX = 0, startIdx = 0, tracking = false, restore, lefts = null;
    // The after-swipe check of the PREVIOUS gesture. It waits for the scroll to
    // go idle - a quick second swipe kept it waiting, so it fired at the end of
    // swipe 2 and pulled the track back to swipe 1's target ("springt auf ein
    // Foto weiter vorne", 2026-10-02). Every new touch cancels it.
    var cancelPending = null;
    // A running goTo() correction. Its end handler forced scrollLeft back to
    // ITS target - when the next swipe started meanwhile, that fired at the
    // end of the new glide and threw the gallery back several photos ("von
    // Bild 5 auf einmal auf Bild 2", 2026-10-12). Every new touch drops it.
    var cancelGo = null;
    // Photo the last swipe is heading for, until its glide has settled. A new
    // swipe started mid-glide counts from there - counting from the photo
    // nearest at that moment (often still the old one) aimed one short, and
    // the after-swipe check then pulled the gallery one photo back.
    var aim = null;

    // Real scroll offset of every visible slide. The product gallery has a
    // column-gap between its slides (and a leading loop clone), so slide k does
    // NOT sit at k * clientWidth — that grid maths drifted one gap further off
    // centre with every swipe. Only used where bySlide is set (the gallery);
    // the card tracks are gap-free and keep the cheap width maths.
    function slideLefts() {
      var tr = track.getBoundingClientRect(), out = [];
      for (var c = track.firstElementChild; c; c = c.nextElementSibling) {
        var r = c.getBoundingClientRect();
        if (r.width < 2) continue; // hidden variant media etc.
        out.push(track.scrollLeft + (r.left - tr.left));
      }
      return out.length ? out : null;
    }
    function nearest(arr, x) {
      var best = 0, d = Infinity;
      for (var i = 0; i < arr.length; i++) {
        var di = Math.abs(arr[i] - x);
        if (di < d) { d = di; best = i; }
      }
      return best;
    }

    // Gesture intent, decided once at the first clear movement (like the
    // browser's own axis lock). A page scroll that drifts a little sideways
    // used to count as a swipe as soon as dx passed 12px — dy was never looked
    // at — so scrolling the grid flipped card photos by accident. Now only a
    // clearly HORIZONTAL gesture advances; a vertical one never does.
    var INTENT_PX = 10;     // movement before the axis is decided
    var H_BIAS = 1.3;       // |dx| must beat |dy| by this factor to be a swipe
    var SWIPE_PX = 24;      // minimum horizontal travel to change photo
    var startY = 0, startLeft = 0, intent = null;

    // Snap comes back only once the glide has really come to rest (scrollend,
    // or no scroll for 150ms) - switching it back on at a fixed 400ms caught
    // slower glides mid-way and the browser re-snapped with a small jolt.
    function goTo(left) {
      if (cancelGo) cancelGo();
      clearTimeout(restore);
      track.style.scrollSnapType = 'none';
      track.scrollTo({ left: left, behavior: 'smooth' });
      var off = function () {
        clearTimeout(restore);
        track.removeEventListener('scroll', onS);
        track.removeEventListener('scrollend', done);
        track.style.scrollSnapType = '';
        cancelGo = null;
      };
      var done = function () {
        off();
        if (Math.abs(track.scrollLeft - left) > 1) track.scrollLeft = left;
      };
      cancelGo = off;
      var onS = function () {
        clearTimeout(restore);
        restore = setTimeout(done, 150);
      };
      track.addEventListener('scroll', onS, { passive: true });
      track.addEventListener('scrollend', done, { once: true });
      restore = setTimeout(done, 900);
    }
    function posOf(idx) {
      if (lefts) return lefts[Math.max(0, Math.min(lefts.length - 1, idx))];
      return idx * widthFn();
    }

    track.addEventListener('touchstart', function (e) {
      // the product gallery on phones runs its own swipe (main-product.liquid)
      if (track.__oaOwnSwipe) { tracking = false; return; }
      if (cancelPending) { cancelPending(); cancelPending = null; }
      if (cancelGo) cancelGo();
      var aimed = aim;
      aim = null;
      if (e.touches.length !== 1) { tracking = false; return; }
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      startLeft = track.scrollLeft;
      intent = null;
      lefts = bySlide ? slideLefts() : null;
      startIdx = lefts ? nearest(lefts, track.scrollLeft) : Math.round(track.scrollLeft / widthFn());
      // still gliding towards the previous swipe's photo: count from there
      // (only if it is a neighbour - the gallery's loop may have hopped from
      // an end clone to its real twin, which changes the index entirely)
      if (aimed !== null && Math.abs(aimed - startIdx) <= 1) startIdx = aimed;
      tracking = true;
    }, { passive: true });

    track.addEventListener('touchmove', function (e) {
      if (!tracking || intent || e.touches.length !== 1) return;
      var ax = Math.abs(e.touches[0].clientX - startX);
      var ay = Math.abs(e.touches[0].clientY - startY);
      if (Math.max(ax, ay) < INTENT_PX) return;
      intent = ax > ay * H_BIAS ? 'x' : 'y';
    }, { passive: true });

    track.addEventListener('touchcancel', function () { tracking = false; }, { passive: true });

    track.addEventListener('touchend', function (e) {
      if (!tracking) return;
      tracking = false;
      var dx = e.changedTouches[0].clientX - startX;
      var dy = e.changedTouches[0].clientY - startY;
      if (!intent) intent = Math.abs(dx) > Math.abs(dy) * H_BIAS ? 'x' : 'y';
      var max = track.scrollWidth - track.clientWidth;
      if (intent !== 'x' || Math.abs(dx) < SWIPE_PX) {
        // Page scroll or tap: stay on the photo we started on. If the track was
        // nudged sideways on the way, put it back rather than letting momentum
        // or snap carry it to the neighbour.
        if (Math.abs(track.scrollLeft - startLeft) > 1) goTo(Math.min(posOf(startIdx), max));
        return;
      }
      var target = startIdx + (dx < 0 ? 1 : -1);
      if (target < 0) target = 0;
      if (lefts && target > lefts.length - 1) target = lefts.length - 1;
      {
        // Product gallery AND card tracks: the browser's own momentum + snap
        // carries the photo (scroll-snap-stop: always - one photo per swipe),
        // which keeps the speed of a fast swipe instead of braking it into our
        // smooth scroll. We only step in afterwards if it came to rest on the
        // wrong photo (skipped one, or stayed put after a clear swipe).
        // The cards used to ALWAYS take over on touchend (snap off + smooth
        // scrollTo from wherever the finger let go): on iOS that kills the
        // native glide and replaces it with a fixed-curve animation - the
        // "ruckelig" grid swipe of 2026-10-02. Now both behave like the gallery.
        var els = [];
        for (var c = track.firstElementChild; c; c = c.nextElementSibling) {
          if (c.getBoundingClientRect().width >= 2) els.push(c);
        }
        if (target > els.length - 1) target = els.length - 1;
        // The product gallery's endless loop takes the step onto an end copy
        // (and the hop back to the real photo) entirely into its own hands
        // (sections/main-product.liquid) - no native glide, no after-check.
        if (track.__oaWrap && track.__oaWrap(els[target])) { aim = null; return; }
        aim = target;
        var targetEl = els[target];
        var srcOf = function (el) {
          var im = el && el.querySelector('img');
          var u = im ? (im.currentSrc || im.src || im.getAttribute('data-oa-src') || '') : '';
          // Same FILE, whatever srcset width was picked: a clone and its real
          // twin can carry different &width= variants of one photo.
          return u.split('?')[0].replace(/^https?:/, '');
        };
        var check = function () {
          track.removeEventListener('scroll', onIdle);
          cancelPending = null;
          aim = null;
          track.__oaCheck = false;
          if (!targetEl) return;
          // Which slide is on screen now? Measured fresh - the gallery's
          // seamless loop may have jumped from an end clone to its real twin
          // meanwhile, which is the same photo and fine.
          var tr = track.getBoundingClientRect(), mid = (tr.left + tr.right) / 2, best = null, bd = Infinity;
          for (var i = 0; i < els.length; i++) {
            var r = els[i].getBoundingClientRect();
            var d = Math.abs((r.left + r.right) / 2 - mid);
            if (d < bd) { bd = d; best = els[i]; }
          }
          if (!best || best === targetEl) return;
          if ((best.hasAttribute('data-oa-clone') || targetEl.hasAttribute('data-oa-clone')) && srcOf(best) && srcOf(best) === srcOf(targetEl)) return;
          // Gallery: measured offset (it has gaps). Cards: width maths - the
          // flip card (.oa-split) runs under CSS zoom, where rects are off.
          var want = lefts
            ? track.scrollLeft + (targetEl.getBoundingClientRect().left - tr.left)
            : posOf(target);
          goTo(Math.min(want, track.scrollWidth - track.clientWidth));
        };
        var idleT = null;
        var onIdle = function () {
          clearTimeout(idleT);
          idleT = setTimeout(check, 160);
        };
        track.addEventListener('scroll', onIdle, { passive: true });
        idleT = setTimeout(check, 400); // no scroll at all (e.g. at an end)
        cancelPending = function () {
          clearTimeout(idleT);
          track.removeEventListener('scroll', onIdle);
          track.__oaCheck = false;
        };
        // The card loop's end-clone hop (settle() in setupTrack) waits for
        // this check: when Safari's glide overshot one photo onto the clone of
        // photo 1, the hop came first, and the check then pulled the gallery
        // from photo 1 all the way back to the photo it aimed for - the
        // "springt auf Bild 1 und wieder zurück" of 2026-10-27. Now the check
        // corrects the overshoot first, then the loop hops.
        track.__oaCheck = true;
      }
    }, { passive: true });
  }

  /* ---- Own touch swipe for the card tracks (touch screens) ----
     Same as the product gallery (sections/main-product.liquid): the browser
     no longer scrolls the photos sideways itself. Its momentum + snapping on
     iPhones kept running underneath every correction and now and then threw
     a card back onto an earlier photo (2026-10-28). The photo follows the
     finger 1:1; on release it glides, starting at the finger's speed, to
     exactly the next / previous photo (or back after a short nudge).
     Vertical page scrolling stays native (touch-action: pan-y); a tap still
     opens the product. Gap-free track: photo k sits at k * width.
     opts.count()  number of slides (incl. loop copies)
     opts.land(i)  called when a glide has ended on slide i */
  /* Moving the photos: during a gesture the slides are SHIFTED with a GPU
     transform (one CSS variable on the track) instead of writing scrollLeft
     every frame - on iPhones every scrollLeft write is a main-thread scroll
     plus scroll events, which is what made the swipe feel heavy / low-fps
     (2026-10-28). When the glide ends the shift is committed to scrollLeft
     in one go and the transform dropped (same frame, no flash). The rule
     only applies while a track carries .oa-tx, so idle cards keep no
     layers. Shared with the product gallery (main-product.liquid). */
  function ensureTxRule() {
    if (document.getElementById('oa-tx-rule')) return;
    var st = document.createElement('style');
    st.id = 'oa-tx-rule';
    st.textContent = '.oa-tx > * { transform: translate3d(var(--oa-tx, 0px), 0, 0); }';
    document.head.appendChild(st);
  }
  window.oaTx = {
    // show the track as if it were scrolled to pos (base = its real scrollLeft)
    set: function (el, base, pos) {
      ensureTxRule();
      if (!el.classList.contains('oa-tx')) el.classList.add('oa-tx');
      el.style.setProperty('--oa-tx', (base - pos).toFixed(2) + 'px');
    },
    // make pos the real scroll position and drop the shift
    commit: function (el, pos) {
      el.scrollLeft = pos;
      el.classList.remove('oa-tx');
      el.style.removeProperty('--oa-tx');
    }
  };

  function ownSwipe(track, widthFn, opts) {
    var st = { on: false, intent: null, x0: 0, y0: 0, base: 0, pos: 0, l0: 0, lastX: 0, lastT: 0, v: 0, i0: 0, anim: 0, target: null };
    track.__oaOwn = st;
    track.style.overflowX = 'hidden';
    track.style.scrollSnapType = 'none';
    track.style.touchAction = 'pan-y';
    track.style.scrollBehavior = 'auto';
    function show(pos) { st.pos = pos; window.oaTx.set(track, st.base, pos); }
    function commit() { window.oaTx.commit(track, st.pos); }
    function animateTo(i, v) {
      cancelAnimationFrame(st.anim); st.anim = 0;
      var to = i * widthFn(), from = st.pos, d = to - from;
      st.target = i;
      var end = function () { st.anim = 0; st.target = null; st.pos = to; commit(); if (opts.land) opts.land(i); };
      if (Math.abs(d) < 1) { end(); return; }
      var sp = Math.abs(v || 0);
      var T = sp > 0.05 ? 3 * Math.abs(d) / sp : Math.abs(d) * 0.9 + 180;
      T = Math.min(520, Math.max(240, T));
      var t0 = null;
      var step = function (now) {
        if (t0 === null) t0 = now;
        var t = Math.min(1, (now - t0) / T), e = 1 - Math.pow(1 - t, 3);
        show(from + d * e);
        if (t < 1) st.anim = requestAnimationFrame(step); else end();
      };
      st.anim = requestAnimationFrame(step);
    }
    function clampI(i) { return Math.max(0, Math.min(opts.count() - 1, i)); }
    track.addEventListener('touchstart', function (e) {
      if (e.touches.length !== 1) { st.on = false; return; }
      var aimAt = null;
      if (st.anim) {
        // a new touch mid-glide: settle the shift where it is, count from
        // where the glide was heading; heading for a loop copy -> move onto
        // the real photo first (same picture)
        cancelAnimationFrame(st.anim); st.anim = 0;
        commit();
        aimAt = st.target; st.target = null;
        if (aimAt !== null && opts.twin) {
          var tw = opts.twin(aimAt);
          if (tw !== aimAt) { track.scrollLeft += (tw - aimAt) * widthFn(); aimAt = tw; }
        }
      }
      var t = e.touches[0];
      st.on = true; st.intent = null;
      st.x0 = st.lastX = t.clientX; st.y0 = t.clientY;
      st.base = st.pos = st.l0 = track.scrollLeft; st.lastT = e.timeStamp; st.v = 0;
      st.i0 = aimAt !== null ? aimAt : Math.round(st.l0 / widthFn());
    }, { passive: true });
    track.addEventListener('touchmove', function (e) {
      if (!st.on || e.touches.length !== 1) return;
      var t = e.touches[0], dx = t.clientX - st.x0, dy = t.clientY - st.y0;
      if (!st.intent) {
        if (Math.max(Math.abs(dx), Math.abs(dy)) < 8) return;
        st.intent = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
        if (st.intent === 'y') { st.on = false; settle(); return; }
      }
      if (e.cancelable) e.preventDefault();
      show(st.l0 - dx);
      var dt = e.timeStamp - st.lastT;
      if (dt > 0) st.v = 0.7 * ((t.clientX - st.lastX) / dt) + 0.3 * st.v;
      st.lastX = t.clientX; st.lastT = e.timeStamp;
    }, { passive: false });
    // a tap or page scroll that stopped a glide half-way: finish it
    function settle() {
      if (Math.abs(st.pos - st.i0 * widthFn()) > 1) animateTo(clampI(st.i0), 0);
    }
    track.addEventListener('touchend', function () {
      if (!st.on) return;
      st.on = false;
      if (st.intent !== 'x') { settle(); return; }
      var dx = st.lastX - st.x0, dir = 0;
      if (Math.abs(dx) > 40 || (Math.abs(st.v) > 0.3 && Math.abs(dx) > 10)) dir = dx < 0 ? 1 : -1;
      animateTo(clampI(st.i0 + dir), (dir !== 0 && (st.v < 0) === (dir > 0)) ? st.v : 0);
    }, { passive: true });
    track.addEventListener('touchcancel', function () {
      if (st.on && st.intent === 'x') { st.on = false; animateTo(clampI(Math.round(st.pos / widthFn())), 0); }
      st.on = false;
    }, { passive: true });
    // a click right after a sideways swipe must not open the product
    track.addEventListener('click', function (e) {
      if (st.intent === 'x' && (st.anim || performance.now() - st.lastT < 400)) { e.preventDefault(); e.stopPropagation(); }
    }, true);
    return st;
  }
  var COARSE = window.matchMedia && window.matchMedia('(hover: none) and (pointer: coarse)').matches;

  /* ---- Mobile in-card swipe track (endless loop) ---- */

  function setupTrack(track) {
    if (track.dataset.oaPh) return;
    track.dataset.oaPh = '1';
    track.classList.add('oa-swipe--fade');

    var real = list(track, '.oa-swipe__slide');
    // In the "Bag It Up" flip card (featured-split, .oa-split) the back grid is
    // rendered with a CSS `zoom`, which makes the track width fractional. The
    // endless-loop LEADING clone (a copy of the LAST image placed before the
    // first, for the backward wrap) then peeks a ~1px sliver on the left at rest.
    // Disable the wrap inside .oa-split so the first slide sits flush at
    // scrollLeft 0 (no clone in front) — the swipe still scrolls, it just doesn't
    // wrap around. Normal collection grids are unaffected and keep the loop.
    var noWrap = !!(track.closest && track.closest('.oa-split'));
    var loop = real.length > 1 && !noWrap;

    // Clone the last slide before the first, and the first slide after the
    // last, so there is always a "next" frame to scroll into at either end.
    if (loop) {
      var lead = real[real.length - 1].cloneNode(true); // clone of last -> front
      var trail = real[0].cloneNode(true);               // clone of first -> back
      lead.dataset.oaClone = '1';
      trail.dataset.oaClone = '1';
      lead.setAttribute('aria-hidden', 'true');
      trail.setAttribute('aria-hidden', 'true');
      track.insertBefore(lead, real[0]);
      track.appendChild(trail);
    }

    var slides = list(track, '.oa-swipe__slide');
    var imgs = slides.map(function (s) { return s.querySelector('img'); });
    imgs.forEach(markLoaded);

    // Swipe indicator (the hairline under the photo, see .oa-swipe-dots in
    // component-card.css). It is a SIBLING of the track, so the clones above
    // never duplicate it, and it holds one segment per REAL slide.
    // Look the bar up from the CARD: with the "below the image" setting it is a
    // sibling of .card__inner, not of the track.
    var dotScope = track.closest('.card-wrapper') || track.closest('.card') || track.parentNode;
    var dotBar = dotScope ? dotScope.querySelector('[data-oa-dots]') : null;
    var segs = dotBar ? list(dotBar, 'span') : [];
    var activeSeg = -1;
    function setDot(idx) {
      if (!segs.length) return;
      if (idx < 0) idx = 0;
      if (idx > segs.length - 1) idx = segs.length - 1;
      if (idx === activeSeg) return;
      if (segs[activeSeg]) segs[activeSeg].classList.remove('is-active');
      segs[idx].classList.add('is-active');
      activeSeg = idx;
      // Instagram-style window: with more than DOT_WINDOW photos only that many
      // dashes are visible and the row slides so the active one stays in the
      // middle (clamped at both ends). The bar keeps its fixed width; the CSS
      // (component-card.css, .oa-swipe-dots:has(> span:nth-child(6))) sizes the
      // dashes and reads --oa-dot-shift. data-oa-edge fades the side(s) that
      // still hide more dashes.
      if (segs.length > DOT_WINDOW) {
        var shift = Math.max(0, Math.min(segs.length - DOT_WINDOW, idx - Math.floor(DOT_WINDOW / 2)));
        dotBar.style.setProperty('--oa-dot-shift', shift);
        var edge = (shift > 0 ? 'l' : '') + (shift < segs.length - DOT_WINDOW ? 'r' : '');
        dotBar.setAttribute('data-oa-edge', edge);
      }
    }
    setDot(0);

    if (!loop) {
      warm(imgs[0]);
      // Flip card (featured-split): no photo swipe there - a sideways swipe
      // turns the card instead (its own script); CSS locks the track.
      if (!noWrap) oneSlidePerSwipe(track, function () { return Math.max(1, track.clientWidth); });
      // No clones here, so the slide index IS the photo index.
      if (segs.length) {
        var ticking0 = false;
        track.addEventListener('scroll', function () {
          if (ticking0) return;
          ticking0 = true;
          requestAnimationFrame(function () {
            ticking0 = false;
            setDot(Math.round(track.scrollLeft / Math.max(1, track.clientWidth)));
          });
        }, { passive: true });
      }
      return;
    }

    // Slide positions after cloning: 0 = clone-of-last, 1..n = real, n+1 = clone-of-first.
    var n = real.length;
    var width = function () { return Math.max(1, track.clientWidth); };
    var current = 1;
    var lastW = 0;

    function jumpTo(px) {
      var prev = track.style.scrollBehavior;
      track.style.scrollBehavior = 'auto';
      track.scrollLeft = px;
      track.style.scrollBehavior = prev || '';
    }

    // Touch screens: our own swipe (see ownSwipe); elsewhere (trackpads in a
    // narrow window) the native scroll with the one-photo guard.
    var own = null;
    if (COARSE) {
      own = ownSwipe(track, width, {
        count: function () { return n + 2; },
        twin: function (i) { return i <= 0 ? n : i >= n + 1 ? 1 : i; },
        land: function () { settle(); }
      });
    } else {
      oneSlidePerSwipe(track, width);
    }

    // Start on the first real slide (position 1), not the leading clone.
    function start() {
      if (track.clientWidth === 0) { requestAnimationFrame(start); return; }
      lastW = width();
      jumpTo(lastW);
    }
    start();

    // Same guard as the product gallery: no clone -> twin hop under the finger.
    var touching = false;
    track.addEventListener('touchstart', function () { touching = true; }, { passive: true });
    var release = function () {
      touching = false;
      clearTimeout(settleTimer);
      settleTimer = setTimeout(settle, 120);
    };
    track.addEventListener('touchend', release, { passive: true });
    track.addEventListener('touchcancel', release, { passive: true });

    function settle() {
      if (touching) return;
      if (own && (own.on || own.anim)) return;
      // a swipe's after-check is still due (oneSlidePerSwipe): it goes first
      if (track.__oaCheck) {
        clearTimeout(settleTimer);
        settleTimer = setTimeout(settle, 120);
        return;
      }
      var w = width();
      var i = Math.round(track.scrollLeft / w);
      if (i <= 0) {
        jumpTo(n * w);   // leading clone (of last) -> real last
        current = n;
      } else if (i >= n + 1) {
        jumpTo(w);       // trailing clone (of first) -> real first
        current = 1;
      } else {
        current = i;
      }
    }

    var settleTimer, ticking = false;
    track.addEventListener('scroll', function () {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(function () {
          ticking = false;
          var i = Math.round(track.scrollLeft / width());
          warm(imgs[i]);
          warm(imgs[i + 1]);
          warm(imgs[i - 1]);
          // Slide positions are 0 = clone-of-last, 1..n = real, n+1 = clone-of-first,
          // so map the clones back onto the real photo they show.
          setDot(i <= 0 ? n - 1 : i >= n + 1 ? 0 : i - 1);
        });
      }
      clearTimeout(settleTimer);
      settleTimer = setTimeout(settle, 120);
    }, { passive: true });

    // Reposition to the same image only when the WIDTH changes (orientation),
    // not on the vertical resizes iOS fires when the URL bar shows/hides.
    var rTimer;
    window.addEventListener('resize', function () {
      clearTimeout(rTimer);
      rTimer = setTimeout(function () {
        var w = width();
        if (w === lastW) return;
        lastW = w;
        jumpTo(Math.min(n, Math.max(1, current)) * w);
      }, 150);
    });

    // Warm the first images and the wrap targets (real first + real last) so
    // wrapping around never lands on a placeholder.
    warm(imgs[1]);
    warm(imgs[2]);
    warm(imgs[n]);
  }

  /* ---- Product-page gallery ---- */

  function galleryImg(slide) {
    return slide
      ? slide.querySelector('.product-media-container.media-type-image .product__media img')
      : null;
  }

  function setupGallery(gallery) {
    if (gallery.dataset.oaPh) return;
    gallery.dataset.oaPh = '1';
    gallery.classList.add('oa-gfade');

    list(gallery, '.product-media-container.media-type-image .product__media img').forEach(markLoaded);

    var slides = list(gallery, '.product__media-list > .product__media-item');
    var slideByMedia = {};
    var active = 0;
    slides.forEach(function (s, i) {
      slideByMedia[s.getAttribute('data-media-id')] = s;
      if (s.classList.contains('is-active')) active = i;
    });

    // Warm the visible slide and its immediate neighbours up front.
    warm(galleryImg(slides[active]));
    warm(galleryImg(slides[active + 1]));
    warm(galleryImg(slides[active - 1]));

    // Desktop: warm a main image the moment its thumbnail is hovered/focused,
    // so the swap on click is instant.
    list(gallery, '.thumbnail-list__item[data-target]').forEach(function (item) {
      var go = function () { warm(galleryImg(slideByMedia[item.getAttribute('data-target')])); };
      item.addEventListener('pointerenter', go, { once: true });
      item.addEventListener('focusin', go, { once: true });
    });

    // Mobile: warm neighbours as the main slider is swiped.
    var track = gallery.querySelector('.product__media-list');
    if (track) {
      // Same one-photo-per-gesture guard as the card swipe: Dawn's slider only
      // sets scroll-snap-align, and WebKit ignores snap stops during momentum,
      // so a firm flick skipped several photos here too. Touch only — the
      // desktop click-and-drag path (.oa-grabbing) is untouched.
      oneSlidePerSwipe(track, function () { return Math.max(1, track.clientWidth); }, true);

      var ticking = false;
      track.addEventListener('scroll', function () {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(function () {
          ticking = false;
          var i = Math.round(track.scrollLeft / Math.max(1, track.clientWidth));
          warm(galleryImg(slides[i]));
          warm(galleryImg(slides[i + 1]));
          warm(galleryImg(slides[i - 1]));
        });
      }, { passive: true });
    }
  }

  /* ---- Scanning ---- */

  function scanCards() {
    if (!MOBILE.matches) return;
    var tracks = list(document, '.oa-swipe');
    if (!tracks.length) return;
    if (!('IntersectionObserver' in window)) {
      tracks.forEach(setupTrack);
      return;
    }
    // display:none tracks (desktop, carousels) never intersect, so they are
    // skipped for free; the rest are set up as they near the viewport.
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) {
          setupTrack(e.target);
          io.unobserve(e.target);
        }
      });
    }, { rootMargin: '300px 0px' });
    tracks.forEach(function (t) { if (!t.dataset.oaPh) io.observe(t); });
  }

  function scanGalleries() {
    list(document, 'media-gallery').forEach(setupGallery);
  }

  /* ---- Desktop hover image ----
   *
   * On desktop a card swaps to its second photo on hover. That image sits in
   * the markup at opacity 0, so the browser has the FILE but usually has not
   * decoded it — the decode then happens in the single frame the hover starts,
   * which is the little hitch you feel on the first hover of each card.
   *
   * So: once a card is near the viewport, make sure its hover image is fetched
   * and DECODED ahead of time. decode() does that off the main thread and
   * resolves when the bitmap is ready, so the hover itself only has to change
   * an opacity.
   *
   * Bounded on purpose: only above 990px (below that there is no hover swap,
   * and the mobile memory budget is the tighter one — see the swipe deferral
   * above), only cards actually approaching the screen, and once per image.
   */
  function warmHoverImages(root) {
    if (MOBILE.matches) return;
    if (!('IntersectionObserver' in window)) return;

    var imgs = list(root || document, '.card-wrapper .media--hover-effect > img + img').filter(
      function (img) { return !img.dataset.oaHoverWarm; }
    );
    if (!imgs.length) return;

    // Fetch + decode one hover image in an idle moment (once per image).
    function warm(img) {
      if (img.dataset.oaHoverWarm) return;
      img.dataset.oaHoverWarm = '1';
      // Wait for an idle moment: the hover image must never compete with the
      // photos the shopper is actually looking at, and nothing is lost by
      // being a second late — the hitch only happens on the FIRST hover.
      var start = function () {
        // Measured on the live grid: these images are not loaded at all
        // (naturalWidth 0), so a hover means fetch AND decode in one frame.
        // Switching off lazy is what starts the fetch.
        if (img.loading === 'lazy') img.loading = 'eager';
        var go = function () {
          if (typeof img.decode !== 'function') return;
          img.decode().catch(function () {});
        };
        if (img.complete) go();
        else img.addEventListener('load', go, { once: true });
      };
      if (typeof requestIdleCallback === 'function') {
        requestIdleCallback(start, { timeout: 2000 });
      } else {
        setTimeout(start, 300);
      }
    }

    // Cards in a moving carousel are NOT observed one by one: the drift moves
    // them every frame, and the browser re-checked every observed hover image
    // each frame (570+ on the homepage, ~3 ms per frame in a lab trace). The
    // carousel itself is observed once; when it nears the screen, the hover
    // image of each product is warmed once (the loop renders every product
    // three times - the copies share the same file, so one each is enough),
    // in idle moments. Static grids (collection pages) keep the per-card
    // observer: nothing moves there.
    var groups = new Map(), loose = [];
    imgs.forEach(function (img) {
      var car = img.closest('simple-carousel');
      if (!car) { loose.push(img); return; }
      if (!groups.has(car)) groups.set(car, []);
      groups.get(car).push(img);
    });

    if (groups.size) {
      var carIo = new IntersectionObserver(
        function (entries, obs) {
          entries.forEach(function (e) {
            if (!e.isIntersecting) return;
            obs.unobserve(e.target);
            var seen = {};
            (groups.get(e.target) || []).forEach(function (img) {
              var key = (img.getAttribute('src') || img.getAttribute('srcset') || '').split(/[?\s]/)[0];
              if (key && seen[key]) { img.dataset.oaHoverWarm = '1'; return; }
              seen[key] = 1;
              warm(img);
            });
          });
        },
        { rootMargin: '100px 0px' }
      );
      // scan() runs again when sections are built later - observe each carousel once
      groups.forEach(function (_, car) { if (car.__oaHoverObs) return; car.__oaHoverObs = true; carIo.observe(car); });
    }

    if (!loose.length) return;
    var io = new IntersectionObserver(
      function (entries, obs) {
        entries.forEach(function (e) {
          if (!e.isIntersecting) return;
          obs.unobserve(e.target);
          warm(e.target);
        });
      },
      // Tight margin on purpose: the homepage carries well over a hundred
      // cards, and warming far-off ones would double the image traffic of a
      // full scroll for nothing.
      { rootMargin: '100px 0px' }
    );
    loose.forEach(function (img) { io.observe(img); });
  }

  function scan() {
    deferSwipeImages(document);
    scanCards();
    scanGalleries();
    warmHoverImages(document);
  }

  // Exposed so the collection "Show More" loader can re-run deferral/setup on
  // the cards it appends (those never fire shopify:section:load).
  window.oaSwipeScan = scan;

  // Exposed for the flip card (featured-split): set its cards up RIGHT NOW
  // instead of waiting for the IntersectionObserver. Its pages live behind a
  // flip / a page change, so a card could still be un-set-up when it appears -
  // the dashes and the fade-in then arrived visibly late. Bounded on purpose:
  // only ever called with the flip card as the root (about 20 cards).
  window.oaSwipeSetupIn = function (root) {
    if (!MOBILE.matches) return;
    deferSwipeImages(root || document);
    list(root || document, '.oa-swipe').forEach(setupTrack);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan, { once: true });
  } else {
    scan();
  }
  // Re-scan when sections re-render (filtering, theme editor) or on breakpoint change.
  document.addEventListener('shopify:section:load', scan);
  if (MOBILE.addEventListener) {
    MOBILE.addEventListener('change', scan);
  } else if (MOBILE.addListener) {
    MOBILE.addListener(scan);
  }
})();

/* Tap feedback on product cards (phones), the same logic as the side menu:
   the tapped card's title + price dip to half ink while the finger is down,
   and a tap that opens the product keeps them dimmed until the next page is
   there (iOS drops :active the instant the finger lifts). A scroll or an
   image swipe is not a tap - moving more than 8px lets go again. A tap on one
   of the meta links (designer, size, brand) dims only that link.
   CSS: .oa-pressed in component-card.css. */
(function () {
  var held = null, sx = 0, sy = 0, nav = false;
  function clear() {
    if (held) held.classList.remove('oa-pressed');
    held = null;
    nav = false;
  }
  function targetOf(e) {
    var a = e.target.closest && e.target.closest('a');
    if (!a) return null;
    // Carousel / collection headings ("Our hottest summer picks" ->) and
    // "View all": the link itself dims, like a menu item.
    if (a.matches('.oa-fc-title-link, .oa-view-all-link, .oa-press')) return a;
    var card = a.closest('.card-wrapper');
    if (!card) return null;
    return a.classList.contains('oa-card-meta__link') ? a : card;
  }
  document.addEventListener('touchstart', function (e) {
    clear();
    if (e.touches.length !== 1) return;
    var t = targetOf(e);
    if (!t) return;
    sx = e.touches[0].clientX;
    sy = e.touches[0].clientY;
    held = t;
    t.classList.add('oa-pressed');
  }, { passive: true });
  document.addEventListener('touchmove', function (e) {
    if (!held) return;
    var dx = Math.abs(e.touches[0].clientX - sx), dy = Math.abs(e.touches[0].clientY - sy);
    if (dx > 8 || dy > 8) clear();
  }, { passive: true });
  document.addEventListener('touchcancel', clear, { passive: true });
  document.addEventListener('touchend', function () {
    if (!held) return;
    var h = held;
    setTimeout(function () { if (held === h && !nav) clear(); }, 400);
  }, { passive: true });
  document.addEventListener('click', function (e) {
    if (!held || !held.contains(e.target)) return;
    nav = true;
    var h = held;
    // Safety net: a link that does not leave the page lets go again.
    setTimeout(function () { if (held === h) clear(); }, 3000);
  }, true);
  window.addEventListener('pageshow', function () {
    document.querySelectorAll('.oa-pressed').forEach(function (el) {
      if (el.closest('.card-wrapper') || el.matches('.oa-fc-title-link, .oa-view-all-link, .oa-press')) el.classList.remove('oa-pressed');
    });
    held = null;
    nav = false;
  });
})();
